import * as React from "react";
import {
  Card,
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Checkbox,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

export type PreviewKind = "image" | "video";

export type FileCardProps = {
  id: string;
  file: File;
  preview?: string;
  busy: boolean;
  onRemove: (id: string) => void;
  onPreview?: (src: string, name: string, kind: PreviewKind) => void;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  isMobile?: boolean;
};

function useInView<T extends Element>() {
  const ref = React.useRef<T | null>(null);
  const [inView, setInView] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView]);
  return { ref, inView };
}

const FileCard = ({
  id,
  file,
  preview,
  busy,
  onRemove,
  onPreview,
  selected = false,
  onToggleSelect,
  isMobile = false,
}: FileCardProps) => {
  const { ref, inView } = useInView<HTMLDivElement>();
  const isImage = file.type.startsWith("image/");
  const isVideo = file.type.startsWith("video/");
  const showOverlay = (isImage || isVideo) && !!preview;
  const mb = (n: number) => (n / 1024 / 1024).toFixed(2);

  const thumbH = isMobile ? 120 : 140;

  return (
    <Card
      elevation={selected ? 3 : 1}
      sx={{
        width: "100%",
        borderRadius: 1.5,
        overflow: "hidden",
        position: "relative",
        outline: selected ? "2px solid" : "none",
        outlineColor: selected ? "primary.main" : "transparent",
        userSelect: "none",
        backgroundColor: "transparent",
      }}
      aria-selected={selected}
    >
      <Box
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          zIndex: 3,
          bgcolor: "rgba(255,255,255,0.9)",
          borderRadius: 1,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          color="primary"
          size={isMobile ? "small" : "medium"}
          checked={selected}
          onChange={() => onToggleSelect?.(id)}
        />
      </Box>
      <Box
        ref={ref}
        sx={(theme) => ({
          height: thumbH,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative",
          backgroundColor: theme.palette.background.default,
          backgroundImage: `
            linear-gradient(45deg, rgba(0,0,0,0.035) 25%, transparent 25%),
            linear-gradient(-45deg, rgba(0,0,0,0.035) 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, rgba(0,0,0,0.035) 75%),
            linear-gradient(-45deg, transparent 75%, rgba(0,0,0,0.035) 75%)
            `,
          backgroundSize: "16px 16px",
          backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
        })}
      >
        {isImage && preview && (
          <img
            src={inView ? preview : undefined}
            alt={file.name}
            loading="lazy"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "cover",
              borderRadius: 8,
            }}
          />
        )}

        {isVideo &&
          (inView && preview ? (
            <video
              src={preview}
              preload="metadata"
              playsInline
              muted
              style={{
                width: "100%",
                maxHeight: "100%",
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
          ) : (
            <Stack alignItems="center" spacing={1} sx={{ p: 2, color: "text.secondary" }}>
              <InsertDriveFileIcon />
              <Typography variant="caption" noWrap>
                {file.name}
              </Typography>
            </Stack>
          ))}

        {!isImage && !isVideo && (
          <Stack alignItems="center" spacing={1} sx={{ p: 2, color: "text.secondary" }}>
            <InsertDriveFileIcon />
            <Typography variant="caption" noWrap>
              {file.name}
            </Typography>
          </Stack>
        )}
        {showOverlay && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onPreview?.(preview!, file.name, isImage ? "image" : "video");
            }}
            role="button"
            aria-label={`Podgląd: ${file.name}`}
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              bgcolor: { xs: "rgba(0,0,0,0.25)", md: "transparent" },
              transition: "background-color .2s",
              "&:hover": {
                bgcolor: "rgba(0,0,0,0.25)",
                "& > .preview-button": { bgcolor: "rgba(255,255,255,1)" },
              },
            }}
          >
            <IconButton
              size="large"
              className="preview-button"
              sx={{
                bgcolor: "rgba(255,255,255,0.85)",
                "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                boxShadow: 2,
              }}
            >
              {isImage ? <ZoomInIcon /> : <PlayCircleOutlineIcon />}
            </IconButton>
          </Box>
        )}
      </Box>

      <Box sx={{ py: 1.25, px: 2 }}>
        <Typography variant="body2" noWrap title={file.name}>
          {file.name}
        </Typography>
        {!isMobile && <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {mb(file.size)} MB
        </Typography>}
      </Box>
      <Box sx={{ px: 1, pb: 1 }}>
        <Tooltip title="Usuń">
          <span style={{ display: "block", width: "100%" }}>
            <Button
              fullWidth
              color="error"
              variant="outlined"
              startIcon={<DeleteOutlineIcon />}
              disabled={busy}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(id);
              }}
              sx={{ borderRadius: 1 }}
            >
              Usuń
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Card>
  );
};

export default React.memo(FileCard);
